const MAX_VISIBILITY_EXPRESSION_LENGTH = 1000;
const MAX_ARITHMETIC_EXPRESSION_LENGTH = 256;

const hasOwn = (target, key) =>
  target !== null &&
  target !== undefined &&
  Object.prototype.hasOwnProperty.call(Object(target), key);

const resolveIdentifier = (identifier, values) => {
  if (identifier === "values") return values;

  if (hasOwn(values, identifier)) {
    return values[identifier];
  }

  const path = identifier.startsWith("values.")
    ? identifier.slice("values.".length).split(".")
    : identifier.split(".");

  let current = values;
  for (const segment of path) {
    if (!segment || !hasOwn(current, segment)) return undefined;
    current = current[segment];
  }
  return current;
};

const readQuotedString = (expression, startIndex) => {
  const quote = expression[startIndex];
  let value = "";
  let index = startIndex + 1;

  while (index < expression.length) {
    const character = expression[index];
    if (character === quote) {
      return { value, nextIndex: index + 1 };
    }
    if (character !== "\\") {
      value += character;
      index += 1;
      continue;
    }

    index += 1;
    if (index >= expression.length) {
      throw new Error("Invalid trailing escape sequence");
    }

    const escaped = expression[index];
    const escapeMap = {
      n: "\n",
      r: "\r",
      t: "\t",
      b: "\b",
      f: "\f",
      v: "\v",
      "0": "\0",
    };
    value += Object.prototype.hasOwnProperty.call(escapeMap, escaped)
      ? escapeMap[escaped]
      : escaped;
    index += 1;
  }

  throw new Error("Unterminated string literal");
};

const tokenizeVisibilityExpression = (expression) => {
  const tokens = [];
  const operators = ["===", "!==", "==", "!=", ">=", "<=", "&&", "||", ">", "<", "!", "(", ")"];
  let index = 0;

  while (index < expression.length) {
    const character = expression[index];
    if (/\s/.test(character)) {
      index += 1;
      continue;
    }

    if (character === "'" || character === '"') {
      const stringToken = readQuotedString(expression, index);
      tokens.push({ type: "literal", value: stringToken.value });
      index = stringToken.nextIndex;
      continue;
    }

    let operator = null;
    for (const candidate of operators) {
      if (expression.startsWith(candidate, index)) {
        operator = candidate;
        break;
      }
    }
    if (operator) {
      tokens.push({ type: "operator", value: operator });
      index += operator.length;
      continue;
    }

    const numberMatch = expression.slice(index).match(/^(?:\d+(?:\.\d*)?|\.\d+)/);
    if (numberMatch) {
      tokens.push({ type: "literal", value: Number(numberMatch[0]) });
      index += numberMatch[0].length;
      continue;
    }

    const identifierMatch = expression.slice(index).match(/^[A-Za-z_$][A-Za-z0-9_$.-]*/);
    if (identifierMatch) {
      const identifier = identifierMatch[0];
      const literals = { true: true, false: false, null: null, undefined };
      if (Object.prototype.hasOwnProperty.call(literals, identifier)) {
        tokens.push({ type: "literal", value: literals[identifier] });
      } else {
        tokens.push({ type: "identifier", value: identifier });
      }
      index += identifier.length;
      continue;
    }

    throw new Error(`Unsupported token at position ${index}`);
  }

  return tokens;
};

const compareValues = (left, operator, right) => {
  switch (operator) {
    case "===":
      return left === right;
    case "!==":
      return left !== right;
    case "==":
      return String(left ?? "") === String(right ?? "");
    case "!=":
      return String(left ?? "") !== String(right ?? "");
    case ">":
      return left > right;
    case ">=":
      return left >= right;
    case "<":
      return left < right;
    case "<=":
      return left <= right;
    default:
      throw new Error("Unsupported comparison operator");
  }
};

const parseVisibilityTokens = (tokens, values) => {
  let index = 0;
  const current = () => tokens[index];
  const consumeOperator = (operator) => {
    if (current()?.type === "operator" && current().value === operator) {
      index += 1;
      return true;
    }
    return false;
  };

  const parsePrimary = () => {
    if (consumeOperator("(")) {
      const value = parseOr();
      if (!consumeOperator(")")) throw new Error("Missing closing parenthesis");
      return value;
    }

    const token = current();
    if (!token || !["literal", "identifier"].includes(token.type)) {
      throw new Error("Expected a value");
    }
    index += 1;
    return token.type === "identifier" ? resolveIdentifier(token.value, values) : token.value;
  };

  const parseUnary = () => (consumeOperator("!") ? !Boolean(parseUnary()) : parsePrimary());

  const parseComparison = () => {
    const left = parseUnary();
    const operator = current();
    const comparisonOperators = new Set(["===", "!==", "==", "!=", ">", ">=", "<", "<="]);
    if (operator?.type !== "operator" || !comparisonOperators.has(operator.value)) {
      return left;
    }
    index += 1;
    return compareValues(left, operator.value, parseUnary());
  };

  const parseAnd = () => {
    let value = parseComparison();
    while (consumeOperator("&&")) {
      const right = parseComparison();
      value = Boolean(value) && Boolean(right);
    }
    return value;
  };

  function parseOr() {
    let value = parseAnd();
    while (consumeOperator("||")) {
      const right = parseAnd();
      value = Boolean(value) || Boolean(right);
    }
    return value;
  }

  const result = parseOr();
  if (index !== tokens.length) throw new Error("Unexpected trailing expression");
  return Boolean(result);
};

export const evaluateVisibilityExpression = (expression, values = {}) => {
  const normalizedExpression = typeof expression === "string" ? expression.trim() : "";
  if (!normalizedExpression) return true;
  if (normalizedExpression.length > MAX_VISIBILITY_EXPRESSION_LENGTH) return false;

  try {
    const tokens = tokenizeVisibilityExpression(normalizedExpression);
    return tokens.length > 0 && parseVisibilityTokens(tokens, values || {});
  } catch {
    return false;
  }
};

const tokenizeArithmeticExpression = (expression) => {
  const tokens = [];
  let index = 0;

  while (index < expression.length) {
    const character = expression[index];
    if (/\s/.test(character)) {
      index += 1;
      continue;
    }
    if ("+-*/%^()".includes(character)) {
      tokens.push(character);
      index += 1;
      continue;
    }
    const numberMatch = expression.slice(index).match(/^(?:\d+(?:\.\d*)?|\.\d+)/);
    if (!numberMatch) throw new Error("Unsupported arithmetic token");
    tokens.push(Number(numberMatch[0]));
    index += numberMatch[0].length;
  }
  return tokens;
};

const parseArithmeticTokens = (tokens) => {
  let index = 0;
  const consume = (token) => {
    if (tokens[index] === token) {
      index += 1;
      return true;
    }
    return false;
  };

  const parsePrimary = () => {
    if (consume("(")) {
      const value = parseAdditive();
      if (!consume(")")) throw new Error("Missing closing parenthesis");
      return value;
    }
    const token = tokens[index];
    if (typeof token !== "number" || !Number.isFinite(token)) {
      throw new Error("Expected a number");
    }
    index += 1;
    return token;
  };

  const parsePower = () => {
    const base = parsePrimary();
    return consume("^") ? base ** parseUnary() : base;
  };

  function parseUnary() {
    if (consume("+")) return parseUnary();
    if (consume("-")) return -parseUnary();
    return parsePower();
  }

  const parseMultiplicative = () => {
    let value = parseUnary();
    while (["*", "/", "%"].includes(tokens[index])) {
      const operator = tokens[index];
      index += 1;
      const right = parseUnary();
      if (operator === "*") value *= right;
      if (operator === "/") value /= right;
      if (operator === "%") value %= right;
    }
    return value;
  };

  function parseAdditive() {
    let value = parseMultiplicative();
    while (["+", "-"].includes(tokens[index])) {
      const operator = tokens[index];
      index += 1;
      const right = parseMultiplicative();
      value = operator === "+" ? value + right : value - right;
    }
    return value;
  }

  const result = parseAdditive();
  if (index !== tokens.length) throw new Error("Unexpected trailing arithmetic expression");
  return result;
};

export const evaluateArithmeticExpression = (expression) => {
  const normalizedExpression = typeof expression === "string" ? expression.trim() : "";
  if (!normalizedExpression || normalizedExpression.length > MAX_ARITHMETIC_EXPRESSION_LENGTH) {
    return null;
  }

  try {
    const result = parseArithmeticTokens(tokenizeArithmeticExpression(normalizedExpression));
    return Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
};
